import { Stack } from "@mui/system";
import * as PDFJS from "pdfjs-dist";
import { PDFPageProxy } from "pdfjs-dist";
import { CSSProperties, useEffect, useMemo, useState, type JSX } from "react";
import { PdfPageThumbnail } from "./pdf-page-thumbnail";

/** Default number of skeletons to display if the PDF is not loaded yet */
const DEFAULT_NB_OF_SKELETONS = 1;

type PdfPageThumbnailListProps = {
  /** The PDF document for which thumbnails should be generated */
  pdfFile: File;
  /** The desired width of the list of thumbnails @default "100%" */
  width?: CSSProperties["width"];
  /** The list of selected PDF pages number (numerated from 1 to numberOfPdfPages) */
  selectedPdfPages?: number[];
  /**
   * callback used to update the page previewed from multi-pages PDF file
   *
   * @param pageNumber selected page number to preview (numerated from 1 to numberOfPdfPages)
   */
  onUpdatePreviewPage?(pageNumber: number): void;
  /** The current page number being previewed */
  previewedPageNumber: number;
  /**
   * callback used to add a page to the selection from multi-pages PDF file
   *
   * @param pageNumber selected page number (numerated from 1 to numberOfPdfPages)
   */
  onAddToSelection?(pageNumber: number): void;
};

/** @returns A list of PDF page thumbnails */
export function PdfPageThumbnailList({
  pdfFile,
  width = "100%",
  onUpdatePreviewPage,
  selectedPdfPages,
  onAddToSelection,
  previewedPageNumber,
}: PdfPageThumbnailListProps): JSX.Element {
  const [numberOfPdfPages, setNumberOfPdfPages] = useState<number>();
  const [allPages, setAllPages] = useState<PDFPageProxy[] | undefined>();

  useEffect(() => {
    async function loadPdf(): Promise<void> {
      try {
        const buffer = await pdfFile.arrayBuffer();
        const pdfDocument = await PDFJS.getDocument({ data: buffer }).promise;
        // With the number of pages, we can create skeletons to display while the thumbnails are loading
        setNumberOfPdfPages(pdfDocument.numPages);
        const pages = await Promise.all(
          Array.from({ length: pdfDocument.numPages }, (_, i) =>
            pdfDocument.getPage(i + 1),
          ),
        );
        // With the pages, we will be able to start loading the thumbnails
        setAllPages(pages);
      } catch (error) {
        throw new Error(`Error loading PDF: ${error}`);
      }
    }
    loadPdf();
  }, [pdfFile, setAllPages, setNumberOfPdfPages]);

  const allPagesInfo = useMemo(
    () =>
      allPages?.map((pageData, index) => ({
        pageData,
        pageNumber: index + 1,
      })),
    [allPages],
  );

  const thumbnails = useMemo(
    () =>
      Array.from(
        { length: numberOfPdfPages ?? DEFAULT_NB_OF_SKELETONS },
        (_, index) => (
          <Stack
            key={index}
            sx={{
              mb: 2,
            }}
          >
            <PdfPageThumbnail
              pageInfo={allPagesInfo?.[index]}
              onUpdatePreviewPage={onUpdatePreviewPage}
              onAddToSelection={onAddToSelection}
              isSelected={!!selectedPdfPages?.includes(index + 1)}
              isPreviewed={previewedPageNumber === index + 1}
            />
          </Stack>
        ),
      ),
    [
      allPagesInfo,
      numberOfPdfPages,
      onAddToSelection,
      onUpdatePreviewPage,
      previewedPageNumber,
      selectedPdfPages,
    ],
  );

  return (
    <Stack
      sx={{
        width,
        height: "65vh",
        pr: 2,
        mr: 2,
        overflowY: "auto",
        scrollbarWidth: "thin",
      }}
    >
      {thumbnails}
    </Stack>
  );
}
